import dayjs from '../lib/dayjs';
import { ValueType } from '../types/ValueType';
import type { IDataTableColumn } from './DataTable.types';

export const isTruthyBool = (value: unknown): boolean =>
    value === true || value === 1 || value === '1' || value === 'true';

// Текст считается на каждую ячейку каждого рендера. toLocaleString создаёт Intl.NumberFormat
// на каждый вызов — форматтеры заводятся один раз (спецификация определяет toLocaleString через
// тот же NumberFormat, текст совпадает).
const moneyFormat = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const doubleFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 6 });
const integerFormat = new Intl.NumberFormat('ru-RU');

// Разбор строки dayjs заметно дороже чтения из Map, а значения дат в колонке повторяются:
// текст запоминается по шаблону и исходной строке (токены шаблонов числовые — от локали
// не зависят). Сверх предела новые значения не запоминаются: очистка на колонке длиннее
// предела на каждом проходе фильтра стирала бы кэш до того, как он пригодится; локальные
// ISO-даты сверх предела считаются нарезкой строки, без dayjs.
const DATE_TEXT_CACHE_LIMIT = 2000;
const dateTextCache = new Map<string, Map<string, string>>();

type DateTemplate = 'DD.MM.YYYY' | 'DD.MM.YYYY HH:mm:ss' | 'DD.MM.YYYY HH:mm';

/** Локальная дата ISO без пояса — `2026-10-25`, `2026-10-25T13:45[:07[.999]]`, через `T` или пробел. */
const LOCAL_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

/**
 * Текст локальной даты ISO нарезкой строки — в несколько раз дешевле dayjs. Нарезка верна, только если dayjs
 * собрал бы из строки ту же дату: части в пределах календаря и момент существует в поясе браузера (не пропущен
 * переводом часов). Иначе — `undefined`, и текст считает dayjs.
 */
const sliceLocalIso = (source: string, template: DateTemplate): string | undefined => {
    const parts = LOCAL_ISO.exec(source);
    if (!parts) return undefined;
    const [, yy, mm, dd, hh = '00', mi = '00', ss = '00'] = parts;
    const year = Number(yy);
    // new Date с годом 0–99 считает его 1900+
    if (year < 100) return undefined;
    const month = Number(mm) - 1;
    const day = Number(dd);
    const hours = Number(hh);
    const minutes = Number(mi);
    const seconds = Number(ss);
    const date = new Date(year, month, day, hours, minutes, seconds);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month ||
        date.getDate() !== day ||
        date.getHours() !== hours ||
        date.getMinutes() !== minutes ||
        date.getSeconds() !== seconds
    ) {
        return undefined;
    }
    const text = `${dd}.${mm}.${yy}`;
    if (template === 'DD.MM.YYYY') return text;
    return template === 'DD.MM.YYYY HH:mm' ? `${text} ${hh}:${mi}` : `${text} ${hh}:${mi}:${ss}`;
};

const formatDateText = (value: unknown, template: DateTemplate): string => {
    const source = String(value);
    let cache = dateTextCache.get(template);
    if (!cache) {
        cache = new Map();
        dateTextCache.set(template, cache);
    }
    let text = cache.get(source);
    if (text === undefined) {
        text = sliceLocalIso(source, template) ?? dayjs(source).format(template);
        if (cache.size < DATE_TEXT_CACHE_LIMIT) cache.set(source, text);
    }
    return text;
};

/** Текст значения по dataType — общий для ячеек, Excel-экспорта и header-фильтра. */
export function formatCellText(value: unknown, dataType?: ValueType): string {
    if (value == null || value === '') return '';
    switch (dataType) {
        case ValueType.Date:
            return formatDateText(value, 'DD.MM.YYYY');
        case ValueType.DateTime:
            return formatDateText(value, 'DD.MM.YYYY HH:mm:ss');
        case ValueType.NoSecDateTime:
            return formatDateText(value, 'DD.MM.YYYY HH:mm');
        case ValueType.Money:
            return moneyFormat.format(Number(value));
        case ValueType.Double:
            return doubleFormat.format(Number(value));
        case ValueType.Integer:
            return integerFormat.format(Number(value));
        case ValueType.Boolean:
            return isTruthyBool(value) ? 'Да' : 'Нет';
        default:
            return String(value);
    }
}

/** Ячейка данных: текст — как есть; число и дата — значением в формате Excel, чтобы по ним считать. */
export type ExcelDataCell = string | { value: number | Date; format?: string };

const NUMBER_TYPES = new Set<ValueType>([ValueType.Money, ValueType.Integer, ValueType.Double]);

const DATE_FORMATS: Partial<Record<ValueType, string>> = {
    [ValueType.Date]: 'dd.mm.yyyy',
    [ValueType.DateTime]: 'dd.mm.yyyy hh:mm:ss',
    [ValueType.NoSecDateTime]: 'dd.mm.yyyy hh:mm',
};

/** Число в строке — только десятичная запись без ведущих нулей и не длиннее 15 цифр до точки (точность Excel):
 *  коды «007», длинные номера счетов, «0x10» и «1e5» остаются текстом. */
const NUMBER_TEXT = /^-?(?:0|[1-9]\d{0,14})(?:\.\d+)?$/;

const RU_DATE_TEXT = /^(\d{2})\.(\d{2})\.(\d{4})(?: (\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/** Раньше 1 марта 1900 номер даты Excel неверен (выдуманное 29.02.1900) или отрицателен. */
const EXCEL_DATES_FROM = Date.UTC(1900, 2, 1);

const toNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value !== 'string') return undefined;
    const text = value.trim();
    return NUMBER_TEXT.test(text) ? Number(text) : undefined;
};

const numberFormat = (dataType: ValueType, number: number): string | undefined => {
    if (dataType === ValueType.Money) return '#,##0.00';
    // дробь в целой колонке (вычисляемая с делением) формат разрядов показал бы округлённой
    return dataType === ValueType.Integer && Number.isInteger(number) ? '#,##0' : undefined;
};

/**
 * Ячейка Excel по типу колонки: число и дата — значением, остальное — текстом formatCellText.
 * Дата собирается из того же текста, что видно в таблице, и кладётся как UTC: Excel хранит дату без пояса,
 * а библиотека записи считает её номер по UTC — иначе часы в файле съехали бы на пояс браузера.
 * `format` — формат ячейки вместо формата типа.
 */
export function excelCell(value: unknown, dataType: ValueType | undefined, format?: string): ExcelDataCell {
    if (value == null || value === '' || dataType === undefined) return formatCellText(value, dataType);
    if (NUMBER_TYPES.has(dataType)) {
        const number = toNumber(value);
        return number === undefined
            ? formatCellText(value, dataType)
            : { value: number, format: format ?? numberFormat(dataType, number) };
    }
    const text = formatCellText(value, dataType);
    const typeDateFormat = DATE_FORMATS[dataType];
    const parts = typeDateFormat ? RU_DATE_TEXT.exec(text) : null;
    if (!parts) return text;
    const [dd, mm, yyyy, hh, mi, ss] = parts.slice(1).map((part) => Number(part ?? 0));
    const date = Date.UTC(yyyy, mm - 1, dd, hh, mi, ss);
    return date < EXCEL_DATES_FROM ? text : { value: new Date(date), format: format ?? typeDateFormat };
}

/** Ячейка выгрузки колонки: значением по `dataType`, а с `cellText` — его текстом, пока колонка не просит
 *  значение (`excelFormat`). Не вышло значения (не число, не дата) — тоже текст `cellText`, как в таблице. */
export function columnExcelCell<T>(column: IDataTableColumn<T> | undefined, row: T, value: unknown): ExcelDataCell {
    if (column?.cellText && !column.excelFormat) return column.cellText(row);
    const cell = excelCell(value, column?.dataType, column?.excelFormat === true ? undefined : column?.excelFormat);
    return typeof cell === 'string' && column?.cellText ? column.cellText(row) : cell;
}
